<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Das Einbetten einer 360°-Tour auf Ihrer Website verwandelt Besucher,
            die bereits auf Ihrer Seite sind, von passiven Interessenten in
            aktive Entdecker Ihrer Räumlichkeiten. Seiten mit eingebetteten
            360°-Touren verzeichnen eine durchschnittliche Sitzungsdauer von
            über 5 Minuten – gegenüber unter 2 Minuten ohne Tour. Und die
            Sitzungsdauer ist eines der wichtigsten Signale im organischen
            Such-Ranking von Google. Dieser Leitfaden behandelt jede
            Einbettungsmethode, plattformspezifische Integration und
            Optimierungstechniken.
          </p>
        </section>

        <section>
          <h2>So funktioniert das Einbetten von 360°-Touren</h2>

          <h3>Die iFrame-Methode</h3>
          <p>
            Der Standardweg, eine 360°-Tour einzubetten, ist ein
            HTML-iFrame-Tag. Ein iFrame bettet eine Webseite in eine andere ein
            – der 360°-Viewer lädt innerhalb des iFrames auf Ihrer Seite. Der
            Tour-Inhalt wird auf der Plattform gehostet (Travvir, Kuula,
            Momento360 usw.) und Ihre Website zeigt ihn einfach über den
            iFrame-Container an.
          </p>
          <p>
            Der grundlegende iFrame-Einbettungscode sieht folgendermaßen aus:
          </p>
          <pre><code>&lt;iframe
  src="https://app.travvir.com/tour/IHRE-TOUR-ID"
  width="100%"
  height="500"
  frameborder="0"
  allowfullscreen
  allow="xr-spatial-tracking; gyroscope; accelerometer"
&gt;&lt;/iframe&gt;</code></pre>
          <p>
            Das Attribut <code>allowfullscreen</code> ermöglicht es dem Viewer,
            sich auf den Vollbildmodus zu erweitern. Das Attribut
            <code>allow</code> gewährt die Berechtigungen, die für die
            gyroskopegesteuerte Anzeige auf Mobilgeräten benötigt werden –
            essenziell für ein gutes mobiles Nutzungserlebnis.
          </p>

          <h3>Einbettungscode von Travvir abrufen</h3>
          <p>
            In Ihrem Travvir-Dashboard: Tour auswählen → Teilen klicken →
            Einbetten auswählen → bereitgestellten iFrame-Code kopieren.
            Travvir's Einbettungscode enthält die korrekten Berechtigungen und
            Seitenverhältniseinstellungen, die bereits vorkonfiguriert sind. Sie
            können die Breiten- und Höhenparameter anpassen, bevor Sie den Code
            in Ihre Seite einfügen.
          </p>
        </section>

        <section>
          <h2>Integration in Website-Plattformen</h2>

          <h3>WordPress</h3>
          <p>
            Im WordPress Block-Editor (Gutenberg): Fügen Sie einen
            „Benutzerdefiniertes HTML"-Block an der gewünschten Stelle ein,
            fügen Sie den iFrame-Einbettungscode ein und veröffentlichen Sie.
            Verwenden Sie nicht den „HTML"-Modus in einem Absatz-Block – dieser
            entfernt iFrame-Tags. Sie müssen den dedizierten HTML-Block
            verwenden.
          </p>
          <p>
            Für WordPress-Seiten mit Page-Buildern (Elementor, Divi, WPBakery):
            Verwenden Sie das HTML-Widget/Modul auf die gleiche Weise – fügen
            Sie ein HTML-Element hinzu und fügen Sie den iFrame direkt ein.
          </p>

          <h3>Squarespace</h3>
          <p>
            Squarespace verwendet einen Einbettungsblock für externe Inhalte.
            Fügen Sie einen Einbettungsblock zu Ihrer Seite hinzu, wechseln Sie
            zur Option „Einbettungscode" (nicht URL-Einbettung, die nur für
            zugelassene Domains funktioniert) und fügen Sie den iFrame-Code ein.
            Squarespace bewahrt den iFrame und rendert ihn korrekt.
          </p>

          <h3>Wix</h3>
          <p>
            Im Wix Editor: Hinzufügen → Einbetten → HTML iFrame →
            Einbettungscode einfügen. Wix verwendet eine sicher abgegrenzte
            Einbettungskomponente, die 360°-Viewer korrekt rendert. Passen Sie
            die Größe der Komponente auf der Arbeitsfläche an Ihre gewünschten
            Maße an.
          </p>

          <h3>Shopify</h3>
          <p>
            Für Produkt- oder Shop-Seiten: Bearbeiten Sie die Seite im
            Code-Editor des Themes (Online-Shop → Themes → Code bearbeiten →
            relevante Template-Datei finden) und fügen Sie den iFrame-HTML
            direkt in das Template ein. Alternativ verwenden Sie Shopify's
            Abschnitt „Benutzerdefiniertes HTML", wenn Ihr Theme dies
            unterstützt.
          </p>

          <h3>Webflow</h3>
          <p>
            Im Webflow Designer: Fügen Sie ein Einbettungselement zu Ihrer
            Arbeitsfläche hinzu und fügen Sie den iFrame-Code in das
            HTML-Einbettungsfeld ein. Webflow rendert iFrames nativ und der
            360°-Viewer funktioniert mit voller Interaktivität, einschließlich
            Gyroskop auf Mobilgeräten.
          </p>
        </section>

        <section>
          <h2>Einbettung für beste Leistung optimieren</h2>

          <h3>Lazy Loading</h3>
          <p>
            Standardmäßig lädt ein iFrame sofort beim Laden der Seite – auch
            wenn der Besucher nie dorthin scrollt, wo die Tour angezeigt wird.
            Das Hinzufügen des Attributs <code>loading="lazy"</code> verzögert
            das Laden, bis der Viewer kurz davor ist, in den Sichtbereich zu
            scrollen:
          </p>
          <pre><code>&lt;iframe
  src="https://app.travvir.com/tour/IHRE-TOUR-ID"
  loading="lazy"
  width="100%"
  height="500"
  frameborder="0"
  allowfullscreen
&gt;&lt;/iframe&gt;</code></pre>
          <p>
            Lazy Loading verbessert die Seitenladegeschwindigkeit für Besucher,
            die den Tour-Bereich nicht erreichen – wichtig für Core Web Vitals
            und Googles Seitenerfahrungs-Signale.
          </p>

          <h3>Responsive Abmessungen</h3>
          <p>
            Fest codierte Pixel-Abmessungen (z. B.
            <code>width="800" height="500"</code>) erstellen ein fest großes
            Einbettbereich, das auf Mobilgeräten überläuft oder beengt aussieht.
            Verwenden Sie einen CSS-Wrapper für responsive Einbettung:
          </p>
          <pre><code>&lt;div style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden;"&gt;
  &lt;iframe
    src="https://app.travvir.com/tour/IHRE-TOUR-ID"
    style="position:absolute; top:0; left:0; width:100%; height:100%; border:0;"
    allowfullscreen
    allow="xr-spatial-tracking; gyroscope; accelerometer"
    loading="lazy"
  &gt;&lt;/iframe&gt;
&lt;/div&gt;</code></pre>
          <p>
            Das <code>padding-bottom: 56.25%</code> erstellt einen
            16:9-Container, der responsiv skaliert. Ändern Sie es auf
            <code>66.66%</code> für ein 3:2-Verhältnis oder <code>75%</code> für
            4:3.
          </p>
        </section>

        <section>
          <h2>Wo Einbetten den größten Effekt hat</h2>

          <h3>Seiten mit dem höchsten Einfluss</h3>
          <p>
            <strong>Startseite:</strong> Für Unternehmen, bei denen der
            physische Raum ein wesentliches Verkaufsargument ist (Hotels,
            Restaurants, Fitnessstudios, Showrooms). Platzieren Sie die Tour
            prominent im Hero-Bereich oder direkt darunter.
          </p>
          <p>
            <strong>Standort-/Kontaktseite:</strong> Einbettungen, die die Frage
            „Wie sieht dieser Ort aus?" beantworten, reduzieren die
            Einstiegshürde für Erstbesucher und verbessern die Anfrage-Rate für
            Wegbeschreibungen.
          </p>
          <p>
            <strong>Immobilien-Listing-Seiten:</strong> Platzieren Sie die Tour
            direkt nach der Fotogalerie. Käufer, die alle Standbilder
            durchgesehen haben, wechseln natürlich zur Tour.
          </p>
          <p>
            <strong>Produkt-/Showroom-Seiten</strong> (Einzelhandel): Einbetten
            auf der relevanten Produkt- oder Kollektionsseite, um den Raum zu
            zeigen, in dem Produkte ausgestellt werden.
          </p>
          <p>
            <strong>Buchungs-/Konversionsseiten:</strong> Die Seite, auf der die
            Kauf- oder Buchungsentscheidung getroffen wird. Eine Tour auf einer
            Hotel-Buchungsseite oder Fitnessstudio-Anmeldeseite gibt Besuchern,
            die kurz vor der Konversion stehen, das nötige letzte Vertrauen.
          </p>
        </section>

        <section>
          <h2>Einbetten in sozialen Medien und anderen Kanälen</h2>

          <h3>Facebook</h3>
          <p>
            Facebook rendert 360°-Fotos nativ als interaktive Beiträge, wenn sie
            direkt hochgeladen werden (nicht als Links). Laden Sie Ihre
            equirektanguläre Bilddatei direkt zu Facebook hoch – es erkennt
            automatisch die 360°-Metadaten und wendet den sphärischen Viewer an.
            Beim Teilen eines Travvir-Tour-Links auf Facebook zeigt die Vorschau
            ein statisches Vorschaubild. Für das interaktive Erlebnis empfiehlt
            es sich, das Quell-360°-Bild direkt hochzuladen.
          </p>

          <h3>Google Business-Profil</h3>
          <p>
            Fügen Sie Ihre 360°-Tour-URL in das Feld „Virtuelle Tour" in Ihrem
            Google Business-Profil ein, um die Funktion „Innen ansehen" in
            Google Maps und lokalen Suchergebnissen zu aktivieren. Dies
            unterscheidet sich vom Einbetten auf Ihrer Website – es platziert
            Ihre Tour direkt in der Google-Sucherfahrung.
          </p>

          <h3>E-Mail-Marketing</h3>
          <p>
            iFrames funktionieren nicht in E-Mail-Clients (die meisten entfernen
            sie aus Sicherheitsgründen). Fügen Sie stattdessen einen Screenshot
            oder animiertes GIF der Tour als E-Mail-Bild ein, mit einem „Tour
            erkunden"-Button, der zur vollständigen Tour-URL führt. Das visuelle
            Preview in der E-Mail treibt Klicks zur Tour auf Ihrer Website.
          </p>
        </section>

        <section>
          <h2>Fazit: Einbetten maximiert den ROI Ihrer Tour</h2>
          <p>
            Eine 360°-Tour, die nur als teilbarer Link existiert, hat begrenzte
            Wirkung. Das direkte Einbetten auf Ihre meistbesuchten Seiten und
            Seiten mit hoher Kaufabsicht stellt die Tour genau im richtigen
            Moment im Entscheidungsprozess vor Besuchern bereit – wenn sie
            bereits auf Ihrer Seite sind und einen Kauf erwägen.
          </p>
          <p>
            Der technische Prozess ist unkompliziert: Kopieren Sie den
            iFrame-Code aus Travvir, fügen Sie ihn in einen HTML-Block auf Ihrer
            Website ein, wenden Sie Lazy Loading und responsiven Wrapper-CSS an
            und platzieren Sie ihn auf den Seiten, wo er den größten
            Konversionseffekt erzielt.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/virtual-tour-wordpress-einbetten">Virtual Tour WordPress einbetten</a></li>
            <li><a href="/blog/de/virtual-tour-seo-leitfaden">Virtual Tour SEO : Rankings verbessern</a></li>
            <li><a href="/blog/de/virtual-tour-zu-immobilienanzeige-hinzufuegen">Virtual Tour zur Immobilienanzeige hinzufügen</a></li>
            <li><a href="/blog/de/360-foto-soziale-medien-teilen">360°-Foto in sozialen Medien teilen</a></li>
            <li><a href="/blog/de/360-virtual-tour-bildgroesse">360°-Virtual-Tour-Bildgröße : Optimale Einstellungen</a></li>
            <li><a href="/blog/de/360-virtual-tour">360° Virtual Tour : Der komplette Leitfaden</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-virtual-tour-website-einbetten",
);

useBlogSeo({
  title: "360°-Virtual-Tour in Website einbetten [2026] | Travvir",
  description:
    "So betten Sie eine 360°-Virtual-Tour in Ihre Website ein. iFrame, WordPress, Wix und individuelle Websites – vollständige Anleitung.",
  slug: "360-virtual-tour-website-einbetten",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
